import type { Denial } from '@/api/denials';
import type { ItemContext, Party } from './context';
import type { Who } from './who';

/**
 * A refusal, said as a record says itself (ADR-0155): which rule held, why
 * it is in front of this reader, who may move it, and what would fix it. A
 * reason is a rule and a rule has an owner, so the catalogue's entries and
 * a refusal a person has just met are explained by the same builder. It
 * never names the reason's code; the gateway's own words and the code are
 * the superadmin's and stay where the page already shows them.
 */

const TONE_WORD: Record<Denial['tone'], string> = {
  danger: 'It crosses a line the platform holds on purpose',
  warning: 'Something has not been granted yet',
  neutral: 'It is a rule working as designed',
};

const askable = (who: string): boolean => !/^nobody\b/i.test(who.trim());
const lowerFirst = (s: string): string => s.charAt(0).toLowerCase() + s.slice(1);

/**
 * A refusal the reader has just met, in the department their session is in,
 * names that department's lead where the rule says "Department lead" — as
 * a person, read from the listing every signed-in person reads. Outside the
 * signed-in frame there is no `Who`, and the rule's own words stand. A rule
 * read from the catalogue is about any department, so it names nobody.
 */
export function leadFor(d: Denial, instance: boolean, w: Who | null | undefined): Party | null {
  if (!instance || !w || d.who.trim() !== 'Department lead') return null;
  return w.lead(w.home);
}

export function explainDenial(
  d: Denial,
  extra: { instance: boolean; resumesAt?: string | null | undefined; to?: string | undefined; who?: Who | null | undefined } = { instance: false },
): ItemContext {
  const lead = leadFor(d, extra.instance, extra.who);
  const yours = lead?.name === 'You';
  const home = extra.who ? extra.who.label(extra.who.home) : 'the department';
  const who = lead ? (yours ? 'You' : `${lead.name}, who ${lead.role ?? 'leads the department'}`) : d.who.trim();
  const ask = askable(who);
  // A rule's words begin a sentence ("Department lead"); a person's name does not change case.
  const whom = lead ? who : lowerFirst(who);
  const tip =
    extra.resumesAt
      ? 'Nothing to do until it opens again: this is a wait, not a fault.'
      : yours
        ? `It is yours to raise, as ${home}’s lead: if the work needs it, ask the superadmin for the grant and say what it is for. Only the superadmin grants it.`
        : d.tone === 'danger'
          ? `Do not look for a way round it: it is a line the platform keeps on purpose. If it is wrong for the work, ${ask ? whom : 'a superadmin'} is the one to say so.`
          : d.tone === 'warning'
            ? ask
              ? `Ask ${whom} for the grant, and say what the work is for. They decide; nothing here can be pressed to get past it.`
              : 'Wait: it opens by itself.'
            : ask
              ? `Nothing is wrong. If the rule is in the way of real work, ask ${whom}.`
              : 'Nothing is wrong. Wait and try again; it opens by itself.';
  return {
    subject: { kind: 'refusal', title: d.title },
    summary: d.body,
    need: yours ? `You can move it: you lead ${home}.` : ask ? `${who} can move it.` : 'Nothing from anyone: it opens by itself.',
    why: `${TONE_WORD[d.tone]}. A refusal names the rule that refused and never what would have been allowed, so that an agent cannot probe for it.`,
    source: { label: 'A rule the gateway checks on every call' },
    ...(extra.instance ? { broughtBy: null } : {}),
    ...(lead ? { owner: { ...lead, role: `${yours ? `lead of ${home}` : (lead.role ?? 'leads the department')} · can move it` } } : ask ? { owner: { name: who, kind: 'person' as const, role: 'can move it' } } : {}),
    next: {
      tip,
      instead: ask ? 'If it was an agent that was refused, the refusal was recorded, and nothing was done.' : 'Nothing was done by the refused call.',
    },
    ...(extra.to ? { open: { to: extra.to, label: 'Open this reason in the list' } } : {}),
  };
}
